import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { UsersRound } from 'lucide-react'
import { accessApi, type Team } from '@/api/accessApi'
import { FormError, ListState } from '@/components/admin/AdminShared'
import { TextAction } from '@/components/admin/catalog/CatalogShared'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAccessMutation, useTeams } from '@/hooks/useAccess'

/** Support teams (teams.manage): add, rename inline, delete only when empty. */
export function TeamsTab() {
  const { t } = useTranslation()
  const teams = useTeams()
  const [name, setName] = useState('')
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState<Team | null>(null)
  const create = useAccessMutation(accessApi.createTeam)
  const rename = useAccessMutation(accessApi.renameTeam)
  const remove = useAccessMutation(accessApi.deleteTeam)

  const add = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim()) create.mutate(name.trim(), { onSuccess: () => setName('') })
  }
  const saveRename = (e: FormEvent) => {
    e.preventDefault()
    if (renaming?.name.trim()) rename.mutate({ id: renaming.id, name: renaming.name.trim() }, { onSuccess: () => setRenaming(null) })
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <form onSubmit={add} className="flex gap-2">
        <Input aria-label={t('access.teams.namePlaceholder')} placeholder={t('access.teams.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          {t('access.teams.new')}
        </Button>
      </form>
      <FormError error={create.error ?? rename.error} />

      <div className="rounded-lg border border-line">
        <ListState query={teams}>
          {(teams.data ?? []).length === 0 ? (
            <EmptyState compact icon={UsersRound} message={t('access.teams.empty')} />
          ) : (
            <ul aria-label={t('access.tabs.teams')}>
              {(teams.data ?? []).map((tm) => {
                const inUse = tm._count.users > 0 || tm._count.tickets > 0
                return (
                  <li key={tm.id} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
                    {renaming?.id === tm.id ? (
                      <form onSubmit={saveRename} className="flex grow items-center gap-2">
                        <Input autoFocus aria-label={t('access.teams.rename')} value={renaming.name} onChange={(e) => setRenaming({ id: tm.id, name: e.target.value })} />
                        <Button type="submit" size="sm" disabled={rename.isPending}>
                          {t('access.teams.save')}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setRenaming(null)}>
                          {t('access.teams.cancel')}
                        </Button>
                      </form>
                    ) : (
                      <>
                        <div className="min-w-0 grow">
                          <p className="truncate text-body font-medium text-ink-strong">{tm.name}</p>
                          <p className="text-label text-ink-muted">
                            {t('access.teams.members', { count: tm._count.users })} · {t('access.teams.tickets', { count: tm._count.tickets })}
                          </p>
                        </div>
                        <TextAction onClick={() => setRenaming({ id: tm.id, name: tm.name })}>{t('access.teams.rename')}</TextAction>
                        <span title={inUse ? t('access.teams.inUse') : undefined}>
                          <TextAction danger disabled={inUse} onClick={() => setDeleting(tm)}>
                            {t('access.teams.delete')}
                          </TextAction>
                        </span>
                      </>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </ListState>
      </div>

      <ConfirmDialog
        open={deleting !== null}
        title={t('access.teams.deleteTitle', { name: deleting?.name ?? '' })}
        body={t('access.teams.deleteBody')}
        confirmLabel={t('access.teams.delete')}
        destructive
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            remove.reset()
          }
        }}
      />
    </div>
  )
}
